import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Eye, Mail, Phone, MapPin, Calendar, DollarSign, MessageSquare, Users, TrendingUp, Home as HomeIcon, Image, MessageCircle } from 'lucide-react';
import axios from 'axios';
import CMSFloorPlans from './CMSFloorPlans';
import CMSPortfolio from './CMSPortfolio';
import CMSTestimonials from './CMSTestimonials';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [leads, setLeads] = useState({
    contacts: [],
    lotEvaluations: [],
    costEstimates: [],
    quickContacts: []
  });
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalLeads: 0,
    todayLeads: 0,
    weekLeads: 0,
    estimatedValue: 0
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/admin/login`, {
        username,
        password
      });

      if (response.data.success) {
        setIsAuthenticated(true);
        toast.success('Welcome to EC Home Builds Admin');
        loadAllLeads();
      } else {
        toast.error(response.data.message || 'Invalid credentials');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadAllLeads = async () => {
    setLoading(true);
    try {
      const [contacts, lotEvals, estimates, quickContacts] = await Promise.all([
        axios.get(`${API}/contact-inquiries`),
        axios.get(`${API}/lot-evaluations`),
        axios.get(`${API}/cost-estimates`),
        axios.get(`${API}/quick-contacts`)
      ]);

      const allLeads = {
        contacts: contacts.data || [],
        lotEvaluations: lotEvals.data || [],
        costEstimates: estimates.data || [],
        quickContacts: quickContacts.data || []
      };

      setLeads(allLeads);
      calculateStats(allLeads);
    } catch (error) {
      console.error('Error loading leads:', error);
      toast.error('Failed to load leads data');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (allLeads) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);

    let todayCount = 0;
    let weekCount = 0;
    let totalValue = 0;

    Object.values(allLeads).flat().forEach(lead => {
      const leadDate = new Date(lead.created_at);
      
      if (leadDate >= today) todayCount++;
      if (leadDate >= weekAgo) weekCount++;
    });

    // Calculate estimated value from cost estimates
    allLeads.costEstimates.forEach(est => {
      if (est.email) { // Only count estimates with contact info
        totalValue += est.estimated_high || 0;
      }
    });

    setStats({
      totalLeads: Object.values(allLeads).flat().length,
      todayLeads: todayCount,
      weekLeads: weekCount,
      estimatedValue: totalValue
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-20 px-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-2xl text-center">EC Home Builds Admin</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Input
                  type="text"
                  placeholder="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </div>
              <div>
                <Input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-[#DAA520] hover:bg-[#B8860B] text-white"
                disabled={loading}
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#262626] mb-2">Admin Dashboard</h1>
          <p className="text-gray-600">Manage your leads and track website performance</p>
          <Button 
            onClick={loadAllLeads} 
            variant="outline" 
            className="mt-4"
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh Data'}
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Leads</p>
                  <p className="text-3xl font-bold text-[#262626]">{stats.totalLeads}</p>
                </div>
                <Users className="text-[#DAA520]" size={40} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Today</p>
                  <p className="text-3xl font-bold text-[#262626]">{stats.todayLeads}</p>
                </div>
                <TrendingUp className="text-[#DAA520]" size={40} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">This Week</p>
                  <p className="text-3xl font-bold text-[#262626]">{stats.weekLeads}</p>
                </div>
                <Calendar className="text-[#DAA520]" size={40} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Est. Pipeline</p>
                  <p className="text-3xl font-bold text-[#262626]">
                    ${(stats.estimatedValue / 1000000).toFixed(1)}M
                  </p>
                </div>
                <DollarSign className="text-[#DAA520]" size={40} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Leads Tabs */}
        <Tabs defaultValue="contacts" className="w-full">
          <TabsList className="grid w-full grid-cols-7 mb-8">
            <TabsTrigger value="contacts">
              Contact Inquiries ({leads.contacts.length})
            </TabsTrigger>
            <TabsTrigger value="lot-eval">
              Lot Evaluations ({leads.lotEvaluations.length})
            </TabsTrigger>
            <TabsTrigger value="estimates">
              Cost Estimates ({leads.costEstimates.length})
            </TabsTrigger>
            <TabsTrigger value="quick">
              Quick Contacts ({leads.quickContacts.length})
            </TabsTrigger>
            <TabsTrigger value="floor-plans" className="bg-[#DAA520]/10">
              Floor Plans (CMS)
            </TabsTrigger>
            <TabsTrigger value="portfolio" className="bg-[#DAA520]/10">
              Portfolio (CMS)
            </TabsTrigger>
            <TabsTrigger value="testimonials" className="bg-[#DAA520]/10">
              Testimonials (CMS)
            </TabsTrigger>
          </TabsList>

          {/* Contact Inquiries */}
          <TabsContent value="contacts">
            <div className="space-y-4">
              {leads.contacts.map((contact) => (
                <Card key={contact.id}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-[#262626] mb-1">{contact.name}</h3>
                        <Badge className="bg-[#DAA520] text-white">{contact.inquiry_type}</Badge>
                      </div>
                      <span className="text-sm text-gray-500">{formatDate(contact.created_at)}</span>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center space-x-2 text-gray-700">
                        <Mail size={16} />
                        <a href={`mailto:${contact.email}`} className="hover:text-[#DAA520]">{contact.email}</a>
                      </div>
                      <div className="flex items-center space-x-2 text-gray-700">
                        <Phone size={16} />
                        <a href={`tel:${contact.phone}`} className="hover:text-[#DAA520]">{contact.phone}</a>
                      </div>
                      {contact.lot_address && (
                        <div className="flex items-center space-x-2 text-gray-700 md:col-span-2">
                          <MapPin size={16} />
                          <span>{contact.lot_address}</span>
                        </div>
                      )}
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm font-semibold text-gray-700 mb-2">Message:</p>
                      <p className="text-gray-600">{contact.message}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {leads.contacts.length === 0 && (
                <Card><CardContent className="p-12 text-center text-gray-500">No contact inquiries yet</CardContent></Card>
              )}
            </div>
          </TabsContent>

          {/* Lot Evaluations */}
          <TabsContent value="lot-eval">
            <div className="space-y-4">
              {leads.lotEvaluations.map((lot) => (
                <Card key={lot.id}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-[#262626] mb-1">{lot.name}</h3>
                        <p className="text-gray-600">{lot.lot_address}</p>
                      </div>
                      <span className="text-sm text-gray-500">{formatDate(lot.created_at)}</span>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center space-x-2 text-gray-700">
                        <Mail size={16} />
                        <a href={`mailto:${lot.email}`} className="hover:text-[#DAA520]">{lot.email}</a>
                      </div>
                      <div className="flex items-center space-x-2 text-gray-700">
                        <Phone size={16} />
                        <a href={`tel:${lot.phone}`} className="hover:text-[#DAA520]">{lot.phone}</a>
                      </div>
                    </div>
                    {lot.message && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-600">{lot.message}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
              {leads.lotEvaluations.length === 0 && (
                <Card><CardContent className="p-12 text-center text-gray-500">No lot evaluations yet</CardContent></Card>
              )}
            </div>
          </TabsContent>

          {/* Cost Estimates */}
          <TabsContent value="estimates">
            <div className="space-y-4">
              {leads.costEstimates.map((est) => (
                <Card key={est.id}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-[#262626] mb-1">
                          {est.sqft} sq ft - {est.style}
                        </h3>
                        <p className="text-2xl font-bold text-[#DAA520]">
                          ${est.estimated_low?.toLocaleString()} - ${est.estimated_high?.toLocaleString()}
                        </p>
                      </div>
                      <span className="text-sm text-gray-500">{formatDate(est.created_at)}</span>
                    </div>
                    {est.name && (
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="flex items-center space-x-2 text-gray-700">
                          <Mail size={16} />
                          <a href={`mailto:${est.email}`} className="hover:text-[#DAA520]">{est.email}</a>
                        </div>
                        <div className="flex items-center space-x-2 text-gray-700">
                          <Phone size={16} />
                          <a href={`tel:${est.phone}`} className="hover:text-[#DAA520]">{est.phone}</a>
                        </div>
                      </div>
                    )}
                    {!est.name && (
                      <p className="text-sm text-gray-500 italic">Anonymous calculation (no contact info provided)</p>
                    )}
                  </CardContent>
                </Card>
              ))}
              {leads.costEstimates.length === 0 && (
                <Card><CardContent className="p-12 text-center text-gray-500">No cost estimates yet</CardContent></Card>
              )}
            </div>
          </TabsContent>

          {/* Quick Contacts */}
          <TabsContent value="quick">
            <div className="space-y-4">
              {leads.quickContacts.map((quick) => (
                <Card key={quick.id}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-[#262626] mb-1">{quick.name}</h3>
                        <Badge className="bg-[#DAA520] text-white">
                          {quick.contact_method === 'call' ? 'Call Me' : 'Text Me'}
                        </Badge>
                      </div>
                      <span className="text-sm text-gray-500">{formatDate(quick.created_at)}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-700 mb-4">
                      <Phone size={16} />
                      <a href={`tel:${quick.phone}`} className="hover:text-[#DAA520]">{quick.phone}</a>
                    </div>
                    {quick.message && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-600">{quick.message}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
              {leads.quickContacts.length === 0 && (
                <Card><CardContent className="p-12 text-center text-gray-500">No quick contacts yet</CardContent></Card>
              )}
            </div>
          </TabsContent>

          {/* CMS Tabs */}
          <TabsContent value="floor-plans">
            <CMSFloorPlans />
          </TabsContent>

          <TabsContent value="portfolio">
            <CMSPortfolio />
          </TabsContent>

          <TabsContent value="testimonials">
            <CMSTestimonials />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
